import React from 'react';
import { ShieldCheck, Satellite } from 'lucide-react';

/**
 * VerifiedBadge — displayed on alerts, detections and observations produced
 * from *real* SkyFi satellite imagery + AI pipeline (as opposed to simulated
 * demonstration scenarios).
 *
 * Companion to SimulationDisclaimer; the two are mutually exclusive.
 */
export function VerifiedBadge({ variant = 'chip', label = 'Verified' }) {
  if (variant === 'ribbon') {
    return (
      <div
        data-testid="verified-ribbon"
        className="w-full bg-emerald-500/10 border-b border-emerald-500/30 text-emerald-800 dark:text-emerald-200 py-1.5 px-4 text-[11px] flex items-center justify-center gap-2"
      >
        <ShieldCheck size={12} className="shrink-0" />
        <span>
          <span className="font-semibold">Verified observation.</span>{' '}
          Generated from live SkyFi satellite imagery (Sentinel-1/-2) and the AI change-detection pipeline.
        </span>
      </div>
    );
  }
  if (variant === 'full') {
    return (
      <div
        data-testid="verified-disclaimer"
        className="rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100 px-3 py-2 text-xs flex items-start gap-2"
      >
        <ShieldCheck size={14} className="shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold">{label}.</span> This record was produced
          from live SkyFi satellite imagery and analysed by the Aakash&nbsp;Drishti AI change-detection pipeline.
        </div>
      </div>
    );
  }
  return (
    <span
      data-testid="verified-chip"
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded px-1.5 py-0.5 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30"
    >
      <ShieldCheck size={10} />
      {label}
    </span>
  );
}

/**
 * QualityTierBadge — communicates whether a real observation is a
 * high-resolution preview (tile-stitched Sentinel raster) or an upgraded
 * full deliverable from a completed SkyFi order.
 */
export function QualityTierBadge({ tier }) {
  const label = tier === 'full' ? 'Full-res' : tier === 'preview' ? 'High-res preview' : 'Pending';
  const cls = tier === 'full'
    ? 'bg-primary/15 text-primary border-primary/30'
    : tier === 'preview'
    ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30'
    : 'bg-muted text-muted-foreground border-border';
  return (
    <span
      data-testid={`quality-tier-${tier || 'unknown'}`}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded px-1.5 py-0.5 border ${cls}`}
    >
      <Satellite size={10} />
      {label}
    </span>
  );
}
